import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Mail, MousePointer, Eye, DollarSign, Users, TrendingUp, ChevronRight, BarChart3, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockCampaigns } from '@/lib/mockData';
import { formatCurrency, formatNumber } from '@/lib/utils';

export function AdminCampaignsDashboard() {
  const navigate = useNavigate();
  
  // Calculate overall metrics
  const totalSent = mockCampaigns.reduce((sum, c) => sum + c.sent, 0);
  const totalOpened = mockCampaigns.reduce((sum, c) => sum + c.opened, 0);
  const totalClicked = mockCampaigns.reduce((sum, c) => sum + c.clicked, 0);
  const totalRevenue = mockCampaigns.reduce((sum, c) => sum + c.revenue, 0);
  const totalConversions = mockCampaigns.reduce((sum, c) => sum + c.conversions, 0);
  
  const overallOpenRate = ((totalOpened / totalSent) * 100).toFixed(1);
  const overallClickRate = ((totalClicked / totalSent) * 100).toFixed(1);
  const activeCampaigns = mockCampaigns.filter(c => c.status === 'active').length;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white">Email Center</h1>
            <p className="text-muted-foreground mt-1 text-sm">Campaign performance & management</p>
          </div>
          <Link to="/admin/campaigns/new/info">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              CREATE CAMPAIGN
            </Button>
          </Link>
        </div>

        {/* Overall Metrics - SHOWN FIRST */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Overall Campaign Metrics
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            <Card className="bg-card border-border p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Total Sent</p>
                  <p className="text-lg lg:text-2xl font-bold text-white">{formatNumber(totalSent)}</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Avg. Open Rate</p>
                  <p className="text-lg lg:text-2xl font-bold text-white">{overallOpenRate}%</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <MousePointer className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Avg. Click Rate</p>
                  <p className="text-lg lg:text-2xl font-bold text-white">{overallClickRate}%</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border p-4 lg:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-lg lg:text-2xl font-bold text-white">{formatCurrency(totalRevenue)}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional metrics row */}
          <div className="grid grid-cols-3 gap-4 lg:gap-6 mt-4">
            <Card className="bg-secondary/30 border-border p-4">
              <p className="text-xs text-muted-foreground">Active Campaigns</p>
              <p className="text-xl lg:text-2xl font-bold text-primary">{activeCampaigns}</p>
            </Card>
            <Card className="bg-secondary/30 border-border p-4">
              <p className="text-xs text-muted-foreground">Total Conversions</p>
              <p className="text-xl lg:text-2xl font-bold text-white">{formatNumber(totalConversions)}</p>
            </Card>
            <Card className="bg-secondary/30 border-border p-4">
              <p className="text-xs text-muted-foreground">Revenue per Email</p>
              <p className="text-xl lg:text-2xl font-bold text-accent">{formatCurrency(totalRevenue / totalSent)}</p>
            </Card>
          </div>
        </div>

        {/* Existing Campaigns List */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Campaigns ({mockCampaigns.length})
          </h2>
          
          <Card className="bg-card border-border overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Campaign</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Sent</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Open Rate</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Click Rate</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Revenue</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {mockCampaigns.map(campaign => {
                    const openRate = ((campaign.opened / campaign.sent) * 100).toFixed(1);
                    const clickRate = ((campaign.clicked / campaign.sent) * 100).toFixed(1);
                    
                    return (
                      <tr 
                        key={campaign.id} 
                        className="border-b border-border hover:bg-secondary/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/campaigns/${campaign.id}/metrics`)}
                        data-testid={`campaign-row-${campaign.id}`}
                      >
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-white">{campaign.name}</p>
                            <p className="text-xs text-muted-foreground">{campaign.type}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            campaign.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {campaign.status === 'active' ? 'Active' : 'Completed'}
                          </span>
                        </td>
                        <td className="p-4 text-white">{formatNumber(campaign.sent)}</td>
                        <td className="p-4 text-blue-400">{openRate}%</td>
                        <td className="p-4 text-accent">{clickRate}%</td>
                        <td className="p-4 text-green-400 font-semibold">{formatCurrency(campaign.revenue)}</td>
                        <td className="p-4">
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile List */}
            <div className="lg:hidden divide-y divide-border">
              {mockCampaigns.map(campaign => {
                const openRate = ((campaign.opened / campaign.sent) * 100).toFixed(1);
                const clickRate = ((campaign.clicked / campaign.sent) * 100).toFixed(1);
                
                return (
                  <div 
                    key={campaign.id}
                    className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/campaigns/${campaign.id}/metrics`)}
                    data-testid={`campaign-row-mobile-${campaign.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{campaign.name}</p>
                        <p className="text-xs text-muted-foreground">{campaign.type}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        campaign.status === 'active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {campaign.status === 'active' ? 'Active' : 'Done'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-muted-foreground">Sent: <span className="text-white">{formatNumber(campaign.sent)}</span></span>
                      <span className="text-muted-foreground">Open: <span className="text-blue-400">{openRate}%</span></span>
                      <span className="text-muted-foreground">Revenue: <span className="text-green-400">{formatCurrency(campaign.revenue)}</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Quick Actions - Create New Campaign */}
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/30 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Ready to launch a new campaign?</h3>
              <p className="text-sm text-muted-foreground">Create targeted email campaigns to boost engagement and sales.</p>
            </div>
            <Link to="/admin/campaigns/new/info">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminCampaignsDashboard;
